
import React, { useState, useMemo, useRef } from 'react';
import * as XLSX from 'xlsx';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  Legend
} from 'recharts';
import { 
  Equipment, 
  InspectionPlan,
  InspectionActual,
  Frequency,
  PlanStatus
} from '../types';
import { 
  Calendar, 
  ChevronLeft, 
  ChevronRight, 
  Plus, 
  Users,
  Clock,
  Filter,
  X,
  MapPin,
  Trash2,
  Edit3,
  AlertCircle,
  FileSpreadsheet,
  CheckCircle2,
  BarChart3,
  Layout,
  RefreshCw,
  Search,
  Activity,
  ChevronRight as ChevronRightIcon
} from 'lucide-react';
import { detectColumns, ColumnMapping } from '../services/geminiService';

interface SchedulePlanProps {
  equipments: Equipment[];
  plans: InspectionPlan[];
  actuals: InspectionActual[];
  addPlan: (plan: InspectionPlan) => void;
  updatePlan: (plan: InspectionPlan) => void;
  deletePlan: (id: string) => void;
  goToInput?: (equipmentId: string) => void;
}

const SchedulePlan: React.FC<SchedulePlanProps> = ({ equipments, plans, actuals, addPlan, updatePlan, deletePlan, goToInput }) => {
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [areaFilter, setAreaFilter] = useState<string>('');
  const [showAnalytics, setShowAnalytics] = useState(true);
  
  const [showForm, setShowForm] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  
  const [editingPlan, setEditingPlan] = useState<InspectionPlan | null>(null);
  const [planToDelete, setPlanToDelete] = useState<InspectionPlan | null>(null);

  const [isImporting, setIsImporting] = useState(false);
  const [importPreview, setImportPreview] = useState<any[]>([]);
  const [importMapping, setImportMapping] = useState<ColumnMapping | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [formData, setFormData] = useState({
    equipmentId: equipments[0]?.id || '',
    plannedDate: new Date().toISOString().split('T')[0],
    pic: '',
    frequency: Frequency.ONCE,
    status: PlanStatus.SCHEDULED
  });

  const monthNames = ["Januari", "Februari", "Maret", "April", "Mei", "Juni", "Juli", "Agustus", "September", "Oktober", "November", "Desember"];

  const uniqueAreas = useMemo(() => {
    return Array.from(new Set(equipments.map(eq => eq.area))).sort();
  }, [equipments]);

  const areaAnalytics = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return uniqueAreas.map(area => {
      const areaEquipIds = equipments.filter(e => e.area === area).map(e => e.id);
      const areaPlans = plans.filter(p => areaEquipIds.includes(p.equipmentId));
      
      let completed = 0;
      let overdue = 0;
      let pending = 0;

      areaPlans.forEach(p => {
        const isCompleted = p.status ? p.status === PlanStatus.COMPLETED : actuals.some(a => a.planId === p.id || (a.equipmentId === p.equipmentId && a.actualDate === p.plannedDate));
        const planDate = new Date(p.plannedDate);
        
        if (isCompleted) completed++;
        else if (planDate < today) overdue++;
        else pending++;
      });

      return {
        name: area,
        'Selesai': completed,
        'Terlambat': overdue,
        'Pending': pending,
        total: areaPlans.length
      };
    }).sort((a, b) => b.total - a.total);
  }, [uniqueAreas, equipments, plans, actuals]);

  const todayStr = new Date().toISOString().split('T')[0];
  const filteredPlans = useMemo(() => {
    return plans.filter(plan => {
      const eq = equipments.find(e => e.id === plan.equipmentId);
      const planDate = new Date(plan.plannedDate);
      const matchesArea = areaFilter === '' || eq?.area === areaFilter;
      const matchesMonth = planDate.getMonth() === currentMonth;
      const matchesYear = planDate.getFullYear() === currentYear;
      return matchesArea && matchesMonth && matchesYear;
    }).sort((a, b) => new Date(a.plannedDate).getTime() - new Date(b.plannedDate).getTime());
  }, [plans, equipments, areaFilter, currentMonth, currentYear]);

  const handlePrevMonth = () => {
    if (currentMonth === 0) { setCurrentMonth(11); setCurrentYear(prev => prev - 1); }
    else setCurrentMonth(prev => prev - 1);
  };

  const handleNextMonth = () => {
    if (currentMonth === 11) { setCurrentMonth(0); setCurrentYear(prev => prev + 1); }
    else setCurrentMonth(prev => prev + 1);
  };

  const handleOpenAdd = () => {
    setEditingPlan(null);
    setFormData({
      equipmentId: equipments[0]?.id || '',
      plannedDate: new Date().toISOString().split('T')[0],
      pic: '',
      frequency: Frequency.ONCE,
      status: PlanStatus.SCHEDULED
    });
    setShowForm(true);
  };

  const handleEditClick = (plan: InspectionPlan) => {
    setEditingPlan(plan);
    setFormData({
      equipmentId: plan.equipmentId,
      plannedDate: plan.plannedDate,
      pic: plan.pic,
      frequency: Frequency.ONCE,
      status: plan.status || PlanStatus.SCHEDULED
    });
    setShowForm(true);
  };

  const handleDeleteClick = (plan: InspectionPlan) => {
    setPlanToDelete(plan);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = () => {
    if (planToDelete) {
      deletePlan(planToDelete.id);
      setShowDeleteConfirm(false);
      setPlanToDelete(null);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (editingPlan) {
      updatePlan({ ...editingPlan, equipmentId: formData.equipmentId, plannedDate: formData.plannedDate, pic: formData.pic, status: formData.status });
    } else {
      let iterations = 1;
      let stepMonths = 0;
      let stepDays = 0;
      switch(formData.frequency) {
          case Frequency.DAILY: iterations = 30; stepDays = 1; break;
          case Frequency.WEEKLY: iterations = 12; stepDays = 7; break;
          case Frequency.MONTHLY: iterations = 12; stepMonths = 1; break;
          case Frequency.QUARTERLY: iterations = 4; stepMonths = 3; break;
          case Frequency.SEMESTERLY: iterations = 2; stepMonths = 6; break;
          case Frequency.YEARLY: iterations = 3; stepMonths = 12; break;
          default: iterations = 1;
      }
      let currentDate = new Date(formData.plannedDate);
      for (let i = 0; i < iterations; i++) {
          addPlan({ 
            id: `plan-${Date.now()}-${i}`, 
            equipmentId: formData.equipmentId, 
            plannedDate: currentDate.toISOString().split('T')[0], 
            pic: formData.pic,
            status: formData.status 
          });
          if (stepMonths > 0) currentDate.setMonth(currentDate.getMonth() + stepMonths);
          else if (stepDays > 0) currentDate.setDate(currentDate.getDate() + stepDays);
          else break;
      }
    }
    setShowForm(false);
  };

  return (
    <div className="space-y-6 pb-20 animate-in fade-in duration-500">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-black text-slate-900 tracking-tighter uppercase">Schedule Plan</h1>
          <p className="text-slate-500 mt-1 font-medium italic">Manajemen jadwal inspeksi rutin peralatan safety.</p>
        </div>
        <div className="flex gap-3">
            <button onClick={() => setShowAnalytics(!showAnalytics)} className={`px-6 py-3 rounded-2xl font-black text-[10px] uppercase tracking-widest flex items-center gap-2 border transition-all shadow-sm ${showAnalytics ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-700 border-slate-200'}`}>
                <BarChart3 className="w-5 h-5" /> {showAnalytics ? 'Hide Stats' : 'Show Stats'}
            </button>
            <button onClick={handleOpenAdd} className="bg-emerald-600 text-white px-8 py-3 rounded-2xl font-black text-[10px] uppercase tracking-widest shadow-xl shadow-emerald-600/20 hover:bg-emerald-700 transition-all flex items-center gap-2">
                <Plus className="w-5 h-5" /> Buat Jadwal
            </button>
        </div>
      </div>

      {showAnalytics && (
        <div className="bg-white p-10 rounded-[3rem] shadow-sm border border-slate-100 animate-in slide-in-from-top-4 duration-500">
            <h3 className="font-black text-slate-800 uppercase tracking-tighter flex items-center gap-3 text-lg mb-8">
                <Layout className="w-6 h-6 text-emerald-500" /> Plant Distribution Index
            </h3>
            <div className="h-[250px] w-full">
                <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={areaAnalytics} layout="vertical" margin={{ left: 10, right: 30 }}>
                        <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} stroke="#f1f5f9" />
                        <XAxis type="number" hide />
                        <YAxis dataKey="name" type="category" width={90} tick={{ fontSize: 10, fontWeight: 900, fill: '#64748b' }} axisLine={false} tickLine={false} />
                        <Tooltip cursor={{ fill: '#f8fafc' }} contentStyle={{ borderRadius: '24px', border: 'none', boxShadow: '0 20px 25px -5px rgb(0 0 0 / 0.1)', fontSize: '11px', fontWeight: 'bold' }} />
                        <Bar dataKey="Selesai" stackId="a" fill="#10b981" barSize={32} />
                        <Bar dataKey="Terlambat" stackId="a" fill="#f43f5e" barSize={32} />
                        <Bar dataKey="Pending" stackId="a" fill="#cbd5e1" radius={[0, 8, 8, 0]} barSize={32} />
                    </BarChart>
                </ResponsiveContainer>
            </div>
        </div>
      )}

      <div className="bg-white p-10 rounded-[3rem] border border-gray-100 shadow-sm min-h-[600px]">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-12">
            <div className="flex items-center gap-4 bg-slate-50 px-8 py-4 rounded-[2rem] border border-slate-100 shadow-inner">
                <h2 className="text-2xl font-black text-slate-800 tracking-tighter uppercase">{monthNames[currentMonth]} {currentYear}</h2>
                <div className="flex gap-2 border-l pl-6 border-slate-200 ml-4">
                    <button onClick={handlePrevMonth} className="p-2.5 bg-white border border-slate-100 shadow-sm rounded-xl text-slate-400 hover:text-emerald-600 transition-all"><ChevronLeft className="w-5 h-5" /></button>
                    <button onClick={handleNextMonth} className="p-2.5 bg-white border border-slate-100 shadow-sm rounded-xl text-slate-400 hover:text-emerald-600 transition-all"><ChevronRight className="w-5 h-5" /></button>
                </div>
            </div>

            <div className="flex items-center gap-4">
                <div className="relative">
                    <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <select value={areaFilter} onChange={(e) => setAreaFilter(e.target.value)} className="pl-12 pr-10 py-4 bg-slate-50 text-slate-700 border border-slate-200 rounded-2xl text-[10px] font-black uppercase tracking-widest outline-none transition-all cursor-pointer min-w-[200px]">
                        <option value="">Semua Area</option>
                        {uniqueAreas.map(area => <option key={area} value={area}>{area}</option>)}
                    </select>
                </div>
                <div className="text-[10px] font-black text-slate-400 uppercase tracking-widest bg-slate-100 px-6 py-4 rounded-2xl border border-slate-200">
                    {filteredPlans.length} ENTRIES
                </div>
            </div>
        </div>

        <div className="space-y-4">
            {filteredPlans.length > 0 ? (
                filteredPlans.map((plan) => {
                    const eq = equipments.find(e => e.id === plan.equipmentId);
                    const isCompleted = plan.status ? plan.status === PlanStatus.COMPLETED : actuals.some(a => a.planId === plan.id || (a.equipmentId === plan.equipmentId && a.actualDate === plan.plannedDate));
                    const today = new Date(); today.setHours(0,0,0,0);
                    const planDate = new Date(plan.plannedDate);
                    const isPastDue = planDate <= today && !isCompleted;

                    return (
                        <div key={plan.id} className={`flex flex-col sm:flex-row items-center p-6 rounded-[2.5rem] border transition-all group ${isPastDue ? 'bg-rose-50 border-rose-100 hover:bg-rose-100' : 'bg-white border-slate-50 hover:border-emerald-100 hover:bg-emerald-50/20'}`}>
                            <div className="flex items-center gap-6 flex-1 w-full">
                                <div className={`w-16 h-16 shrink-0 flex flex-col items-center justify-center border-r-2 ${isPastDue ? 'border-rose-200' : 'border-slate-100'}`}>
                                    <p className={`text-3xl font-black tracking-tighter ${isPastDue ? 'text-rose-600' : 'text-slate-800'}`}>{plan.plannedDate.split('-')[2]}</p>
                                    <p className={`text-[9px] font-black uppercase tracking-widest ${isPastDue ? 'text-rose-400' : 'text-slate-400'}`}>{monthNames[new Date(plan.plannedDate).getMonth()].substring(0, 3)}</p>
                                </div>
                                <div className="flex-1 space-y-2">
                                    <div className="flex flex-wrap items-center gap-3">
                                        <span className={`px-3 py-1 rounded-lg text-[9px] font-black tracking-widest uppercase ${isPastDue ? 'bg-rose-600 text-white' : 'bg-slate-900 text-white'}`}>{eq?.code}</span>
                                        <h4 className={`text-base font-black tracking-tight ${isPastDue ? 'text-rose-900' : 'text-slate-800'}`}>{eq?.type}</h4>
                                        <span className="flex items-center gap-1.5 text-[9px] font-black uppercase text-slate-400 bg-white/50 px-3 py-1 rounded-lg border border-slate-100">
                                          <MapPin className="w-3 h-3" /> {eq?.area}
                                        </span>
                                        {plan.status === PlanStatus.COMPLETED ? (
                                          <span className="flex items-center gap-2 px-4 py-1.5 bg-emerald-600 text-white rounded-xl text-[9px] font-black uppercase tracking-widest shadow-lg shadow-emerald-600/20">
                                            <CheckCircle2 className="w-3.5 h-3.5" /> COMPLETED
                                          </span>
                                        ) : isPastDue ? (
                                          <div className="flex items-center gap-2">
                                            <span className="flex items-center gap-2 px-4 py-1.5 bg-rose-600 text-white rounded-xl text-[9px] font-black uppercase tracking-widest shadow-lg shadow-rose-600/20 animate-pulse">
                                              <AlertCircle className="w-3.5 h-3.5" /> {plan.plannedDate === todayStr ? 'DUE TODAY' : 'OVERDUE'}
                                            </span>
                                            <button 
                                              onClick={() => goToInput?.(plan.equipmentId)}
                                              className="flex items-center gap-2 px-4 py-1.5 bg-white border border-rose-200 text-rose-600 rounded-xl text-[9px] font-black uppercase tracking-widest shadow-sm hover:bg-rose-600 hover:text-white transition-all active:scale-95"
                                            >
                                              {plan.plannedDate === todayStr ? 'Start Inspection' : 'Follow Up'} <ChevronRightIcon className="w-3.5 h-3.5" />
                                            </button>
                                          </div>
                                        ) : plan.status === PlanStatus.IN_PROGRESS ? (
                                          <span className="flex items-center gap-2 px-4 py-1.5 bg-indigo-600 text-white rounded-xl text-[9px] font-black uppercase tracking-widest shadow-lg shadow-indigo-600/20 animate-pulse">
                                            <Activity className="w-3.5 h-3.5" /> IN PROGRESS
                                          </span>
                                        ) : (
                                          <span className="flex items-center gap-2 px-4 py-1.5 bg-slate-100 text-slate-500 rounded-xl text-[9px] font-black uppercase tracking-widest border border-slate-200">
                                            <Clock className="w-3.5 h-3.5" /> PENDING
                                          </span>
                                        )}
                                    </div>
                                    <div className="flex items-center gap-6">
                                        <div className={`flex items-center gap-2 text-[9px] font-black uppercase tracking-widest ${isPastDue ? 'text-rose-400' : 'text-slate-400'}`}><Clock className="w-3.5 h-3.5" /> {eq?.frequency}</div>
                                        <div className={`flex items-center gap-2 text-[9px] font-black uppercase tracking-widest ${isPastDue ? 'text-rose-400' : 'text-slate-400'}`}><Users className="w-3.5 h-3.5" /> PIC: {plan.pic}</div>
                                    </div>
                                </div>
                            </div>
                            <div className="flex items-center gap-2 mt-4 sm:mt-0 opacity-0 group-hover:opacity-100 transition-all">
                                <button onClick={() => handleEditClick(plan)} className="p-3 text-slate-300 hover:text-emerald-600 hover:bg-white rounded-2xl shadow-sm transition-all border border-transparent"><Edit3 className="w-5 h-5" /></button>
                                <button onClick={() => handleDeleteClick(plan)} className="p-3 text-slate-300 hover:text-rose-600 hover:bg-white rounded-2xl shadow-sm transition-all border border-transparent"><Trash2 className="w-5 h-5" /></button>
                            </div>
                        </div>
                    );
                })
            ) : (
                <div className="py-24 text-center opacity-30 italic font-bold">Belum ada rencana inspeksi periode ini.</div>
            )}
        </div>
      </div>

      {/* MODAL FORM CREATE/EDIT */}
      {showForm && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-6 bg-slate-900/60 backdrop-blur-xl animate-in fade-in duration-300">
              <div className="bg-white rounded-[4rem] w-full max-w-lg shadow-2xl overflow-hidden scale-in">
                  <form onSubmit={handleSubmit}>
                    <div className="p-10 border-b border-gray-100 flex justify-between items-center bg-slate-50">
                        <h3 className="text-2xl font-black text-slate-900 uppercase tracking-tighter">{editingPlan ? 'Edit Jadwal' : 'Tambah Rencana Jadwal'}</h3>
                        <button type="button" onClick={() => setShowForm(false)} className="p-4 text-slate-400 hover:text-rose-500 bg-white rounded-2xl shadow-sm"><X className="w-8 h-8" /></button>
                    </div>
                    <div className="p-10 space-y-8 max-h-[60vh] overflow-y-auto">
                        <div className="space-y-2">
                            <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest ml-1">Pilih Peralatan</label>
                            <select required disabled={!!editingPlan} value={formData.equipmentId} onChange={e => setFormData({...formData, equipmentId: e.target.value})} className="w-full px-6 py-4 bg-slate-50 border border-slate-200 rounded-2xl font-bold text-sm outline-none focus:ring-4 focus:ring-emerald-500/10 transition-all">
                                {equipments.map(eq => <option key={eq.id} value={eq.id}>{eq.code} - {eq.type} ({eq.area})</option>)}
                            </select>
                        </div>
                        {!editingPlan && (
                            <div className="space-y-2">
                                <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest ml-1">Otomasi Jadwal Berulang</label>
                                <select value={formData.frequency} onChange={e => setFormData({...formData, frequency: e.target.value as Frequency})} className="w-full px-6 py-4 bg-emerald-50 border border-emerald-100 rounded-2xl font-black text-xs uppercase tracking-widest outline-none">
                                    {Object.values(Frequency).map(f => <option key={f} value={f}>{f}</option>)}
                                </select>
                            </div>
                        )}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                            <div className="space-y-2">
                                <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest ml-1">Target Tanggal</label>
                                <input required type="date" value={formData.plannedDate} onChange={e => setFormData({...formData, plannedDate: e.target.value})} className="w-full px-6 py-4 bg-slate-50 border border-slate-200 rounded-2xl font-bold text-sm outline-none" />
                            </div>
                            <div className="space-y-2">
                                <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest ml-1">Status Awal</label>
                                <select value={formData.status} onChange={e => setFormData({...formData, status: e.target.value as PlanStatus})} className="w-full px-6 py-4 bg-slate-50 border border-slate-200 rounded-2xl font-bold text-sm outline-none">
                                    {Object.values(PlanStatus).map(s => <option key={s} value={s}>{s}</option>)}
                                </select>
                            </div>
                        </div>
                        <div className="space-y-2">
                            <label className="text-[10px] font-black uppercase text-slate-400 tracking-widest ml-1">PIC Inspektur</label>
                            <input required type="text" value={formData.pic} onChange={e => setFormData({...formData, pic: e.target.value})} className="w-full px-6 py-4 bg-slate-50 border border-slate-200 rounded-2xl font-bold text-sm outline-none" placeholder="Nama PIC" />
                        </div>
                    </div>
                    <div className="p-10 bg-slate-50 border-t border-gray-100 flex justify-end gap-4">
                        <button type="button" onClick={() => setShowForm(false)} className="px-10 py-5 bg-white border border-slate-200 text-slate-500 rounded-3xl font-black text-xs uppercase tracking-widest">Batal</button>
                        <button type="submit" className="px-16 py-5 bg-slate-900 text-white rounded-3xl font-black text-xs uppercase tracking-widest shadow-2xl hover:bg-emerald-600 transition-all">
                            {editingPlan ? 'Update Jadwal' : 'Simpan Jadwal'}
                        </button>
                    </div>
                  </form>
              </div>
          </div>
      )}

      {/* MODAL KONFIRMASI HAPUS (FIXED) */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-6 bg-slate-950/80 backdrop-blur-xl animate-in fade-in duration-300">
          <div className="bg-white rounded-[3rem] w-full max-w-md shadow-2xl p-12 text-center scale-in">
            <div className="w-24 h-24 bg-rose-50 text-rose-500 rounded-[2rem] flex items-center justify-center mx-auto mb-8 shadow-inner">
              <Trash2 className="w-12 h-12" />
            </div>
            <h3 className="text-2xl font-black text-slate-900 uppercase tracking-tighter mb-4">Hapus Rencana Jadwal?</h3>
            <p className="text-slate-500 font-medium mb-10 leading-relaxed italic">
              Tindakan ini akan menghapus jadwal inspeksi untuk aset <span className="text-slate-900 font-bold">{equipments.find(e => e.id === planToDelete?.equipmentId)?.code}</span> secara permanen.
            </p>
            <div className="flex flex-col gap-4">
              <button onClick={confirmDelete} className="w-full py-5 bg-rose-600 text-white rounded-3xl font-black text-xs uppercase tracking-widest shadow-xl shadow-rose-600/20 hover:bg-rose-700 transition-all">
                Ya, Hapus Sekarang
              </button>
              <button onClick={() => setShowDeleteConfirm(false)} className="w-full py-5 bg-slate-100 text-slate-500 rounded-3xl font-black text-xs uppercase tracking-widest hover:bg-slate-200 transition-all">
                Batalkan
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SchedulePlan;
